/*
 * Admin assistant chat transport + tool protocol (NEXUS-1384).
 *
 * Talks to Nexus /v1/chat (appId aquastar, the signed-in staff member's bearer) — the same
 * endpoint and SSE shape the customer Splash assistant uses. Tools follow Splash's fenced-block
 * protocol: the model replies with a ```tool_call``` JSON fence, the console runs the tool and
 * sends back a ```tool_result``` fence, the model then answers in prose.
 *
 * The only tool is run_report — a named report (lib/reports.ts) with a day window. The model
 * never sees SQL or ids it could misuse; the server validates report + params again.
 */

import { authedFetch, getToken } from './apiClient';
import { APP_ID, GATEWAY_BASE } from './config';
import { isReportId, type ReportId } from './reports';

export interface ChatTurn {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface ReportCatalogueEntry {
  id: string;
  title: string;
  description: string;
  params: string[];
}

/** Mirrors App-Definitions jobs/_aquastar-reports.js; replaced by the live catalogue when it loads. */
export const FALLBACK_CATALOGUE: ReportCatalogueEntry[] = [
  {
    id: 'faults_by_pump',
    title: 'Pumps with faults',
    description:
      'Every pump that reported a fault in the window, with how many faults, how many are still open and when the last one appeared.',
    params: ['days'],
  },
  {
    id: 'faults_by_code',
    title: 'Faults by code',
    description:
      'How often each fault code occurred in the window, with its description and how many pumps it hit.',
    params: ['days'],
  },
  {
    id: 'faults_per_day',
    title: 'Faults per day',
    description: 'New faults per day across the fleet in the window (a trend line).',
    params: ['days'],
  },
  {
    id: 'commands_by_user',
    title: 'Pump commands by user',
    description:
      'Who controlled pumps from the cloud in the window (power, speed, schedule, clock), how many commands and on how many pumps.',
    params: ['days'],
  },
  {
    id: 'pump_overview',
    title: 'Pump overview',
    description: 'Everything about ONE pump in one card: status, speed, power, last seen, faults, last command, firmware. Takes "pump" (name, part of the name, or id).',
    params: ['pump', 'days'],
  },
  {
    id: 'pumps_offline',
    title: 'Pumps offline',
    description: 'Pumps not heard from for at least N hours, longest first.',
    params: ['hours'],
  },
  {
    id: 'firmware_versions',
    title: 'Firmware versions',
    description: 'Which Wi-Fi and board firmware each pump runs, and how many pumps are on each version.',
    params: [],
  },
  {
    id: 'firmware_updates',
    title: 'Firmware updates',
    description: 'Over-the-air firmware update attempts in the window, with from/to version and result.',
    params: ['days'],
  },
  {
    id: 'open_faults',
    title: 'Open faults',
    description: 'Faults that have not cleared yet, one row per pump and code. Ignores days.',
    params: [],
  },
];

/** Reports the console runs itself (not in the server's catalogue) — always offered. */
export const CLIENT_CATALOGUE: ReportCatalogueEntry[] = [
  {
    id: 'support_issues',
    title: 'Open support issues',
    description:
      'Open issues on the Issues list (the issue tracker: bugs, support requests, follow-ups) — NOT pump faults. Most severe first, with status, type, whether assigned and age in days. Ignores days.',
    params: [],
  },
];

/** Server catalogue + the console's own reports (no duplicates). */
export function withClientReports(server: ReportCatalogueEntry[]): ReportCatalogueEntry[] {
  const ids = new Set(server.map((r) => r.id));
  return [...server, ...CLIENT_CATALOGUE.filter((r) => !ids.has(r.id))];
}

export const ISSUES_CLARIFY = {
  question: 'Do you mean problems with the pumps, or issues on the Issues list?',
  options: ['Pump faults that are still open', 'Open issues on the Issues list'],
};

/**
 * "What are the top open issues?" — "issues" alone could mean pump faults or tickets on the Issues
 * list. Ask instead of guessing, unless the question already says which (fault/pump/error/alarm vs
 * ticket/support/tracker/issues list/bug), or is about creating one. The caller also skips it on a
 * pump page and right after a clarifying question (so it can never loop).
 */
export function needsIssuesClarification(text: string): boolean {
  const t = text.toLowerCase();
  if (!/\bissues?\b/.test(t)) return false;
  if (/\b(create|file|log|raise|submit|add|make)\s+(an?\s+|the\s+|new\s+)?(issue|ticket|bug)\b/.test(t)) return false;
  if (/\b(faults?|pumps?|errors?|alarms?|codes?|offline|firmware|wi-?fi|connect\w*)\b/.test(t)) return false;
  if (/\b(tickets?|support|tracker|issues? list|bugs?|assigned|assignee|unassigned|severity|backlog|roadmap|[a-z]+-\d+)\b/.test(t)) return false;
  return true;
}

function paramHelp(params: string[]): string {
  const out: string[] = [];
  if (params.includes('pump')) out.push('"pump" (name or id)');
  if (params.includes('days')) out.push('"days" (1-365, default 30)');
  if (params.includes('hours')) out.push('"hours" (1-720, default 24)');
  return out.length ? ` Takes ${out.join(', ')}.` : '';
}

export function buildSystemPrompt(input: {
  catalogue: ReportCatalogueEntry[];
  staffName: string | null;
  role: string | null;
  page: string;
  stage: string;
  now?: Date;
  /** The pump the user is looking at (pump detail page), if any. */
  contextPump?: string | null;
  /** Whether the create_issue tool is offered. */
  canCreateIssues?: boolean;
}): string {
  const now = input.now ?? new Date();
  const reports = input.catalogue
    .map(
      (r) =>
        `- ${r.id} — ${r.title}. ${r.description}${paramHelp(r.params)}`,
    )
    .join('\n');
  return [
    'You are the AquaStar admin console assistant. You help AquaStar staff understand their pool-pump fleet.',
    `Today is ${now.toISOString().slice(0, 10)}. Environment: ${input.stage}. The user is ${input.staffName ?? 'a staff member'}${input.role ? ` (role: ${input.role})` : ''}, currently on the ${input.page} page.`,
    ...(input.contextPump
      ? [`The user is looking at pump ${input.contextPump}. When they say "this pump" or "it", use "pump":"${input.contextPump}".`]
      : []),
    '',
    'You can run fleet reports. To run one, reply with ONLY this fenced block and nothing else:',
    '```tool_call',
    '{"tool":"run_report","args":{"report":"faults_by_pump","days":30}}',
    '```',
    'Available reports:',
    reports,
    '',
    'Rules:',
    '- When the user asks for fleet numbers, faults, errors, trends or "show me", run the matching report. Turn "last month" into 30 days, "last week" into 7, "this year" into the days since January 1.',
    '- After the report runs you get a ```tool_result``` block. The user already sees it as a chart and a grid with a "Pin to dashboard" button, so do NOT repeat the table. Answer in 1-3 short sentences: the headline numbers and anything that stands out. Mention they can pin it to the dashboard if it is worth watching.',
    '- Never invent numbers; only use what a tool_result contains. If no report covers the question, say which reports you can run.',
    '- If a request could mean two different things, ask ONE short clarifying question instead of guessing, offering the likely answers. Reply with ONLY:',
    '```tool_call',
    '{"tool":"ask_user","args":{"question":"Do you mean problems with the pumps, or issues on the Issues list?","options":["Pump faults that are still open","Open issues on the Issues list"]}}',
    '```',
    '  Typical case: "issues"/"problems" alone — pump faults (fault reports) vs. tickets on the Issues list (support_issues). Do not ask when the question already says which.',
    ...(input.canCreateIssues
      ? [
          '- You can PROPOSE a support issue (bug, follow-up) for a person to confirm — it is only created when they press Create. Reply with ONLY:',
          '```tool_call',
          '{"tool":"create_issue","args":{"title":"Pump A840 reports NF faults daily","description":"What happened, which pump(s), what the report showed.","severity":"medium"}}',
          '```',
          '  Use it only when the user asks to file / create / log an issue or ticket. Keep the title under 100 characters.',
        ]
      : []),
    '- You cannot control pumps or change settings from here.',
    '- The user may attach documents; their text arrives in ```attachment name="…"``` blocks inside their message. Use them when they are relevant and say so if one looks cut off.',
    '- Plain, friendly English. Light markdown is fine (bold, short bullet lists); no markdown tables — the console already shows report tables.',
  ].join('\n');
}

export type ToolCall =
  | { tool: 'run_report'; report: ReportId; days?: number; hours?: number; pump?: string }
  | { tool: 'create_issue'; title: string; description: string; severity?: string }
  | { tool: 'ask_user'; question: string; options: string[] };

const FENCE_RE = /```(?:tool_call|json)?\s*(\{[\s\S]*?\})\s*```/i;
const TAG_RE = /<tool_call>\s*(\{[\s\S]*?\})\s*<\/tool_call>/i;

/** The model's tool call, if its reply contains one (fence, bare <tool_call> tag, or bare JSON). */
export function parseToolCall(text: string): ToolCall | null {
  const candidates: string[] = [];
  const f = FENCE_RE.exec(text);
  if (f) candidates.push(f[1]!);
  const t = TAG_RE.exec(text);
  if (t) candidates.push(t[1]!);
  const trimmed = text.trim();
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) candidates.push(trimmed);
  for (const c of candidates) {
    try {
      const j = JSON.parse(c) as Record<string, unknown>;
      const args = (j.args ?? j.arguments ?? j.parameters ?? {}) as Record<string, unknown>;
      const name = j.tool ?? j.name;
      if (name === 'ask_user') {
        const question = String(args.question ?? j.question ?? '').trim().slice(0, 300);
        const raw = (args.options ?? j.options ?? []) as unknown;
        const options = Array.isArray(raw)
          ? raw.map((o) => String(o ?? '').trim().slice(0, 120)).filter(Boolean).slice(0, 4)
          : [];
        if (!question) continue;
        return { tool: 'ask_user', question, options };
      }
      if (name === 'create_issue') {
        const title = String(args.title ?? '').trim().slice(0, 140);
        if (!title) continue;
        const sev = String(args.severity ?? '').trim().toLowerCase();
        return {
          tool: 'create_issue',
          title,
          description: String(args.description ?? '').slice(0, 4000),
          severity: /^[a-z_]{2,20}$/.test(sev) ? sev : undefined,
        };
      }
      const report = args.report ?? j.report;
      if ((name === 'run_report' || report !== undefined) && isReportId(report)) {
        const days = Number(args.days ?? j.days);
        const hours = Number(args.hours ?? j.hours);
        const pumpRaw = String(args.pump ?? j.pump ?? '').trim().slice(0, 80);
        return {
          tool: 'run_report',
          report,
          days: Number.isFinite(days) && days > 0 ? Math.min(365, Math.round(days)) : undefined,
          ...(Number.isFinite(hours) && hours > 0 ? { hours: Math.min(720, Math.round(hours)) } : {}),
          ...(pumpRaw ? { pump: pumpRaw } : {}),
        };
      }
    } catch {
      /* not JSON — keep looking */
    }
  }
  return null;
}

/** What the user sees of an assistant reply: no tool fences, no reasoning blocks. */
export function visibleText(text: string): string {
  let t = text
    .replace(/<think>[\s\S]*?(<\/think>|$)/gi, '')
    // Any fenced block carrying a tool call (```tool_call, ```json, bare ```) or a result.
    .replace(/```(?:tool_call|tool_result|json)?\s*\{[\s\S]*?(```|$)/gi, '')
    .replace(/```(?:tool_call|tool_result)[\s\S]*?(```|$)/gi, '')
    .replace(/<tool_call>[\s\S]*?(<\/tool_call>|$)/gi, '');
  // Mid-stream: a fence that has only just opened ("```tool_c") — hide it until it resolves.
  t = t.replace(/```[a-z_]*\s*$/i, '').replace(/<tool[a-z_]*>?\s*$/i, '');
  const trimmed = t.trim();
  // A reply that is nothing but a bare JSON tool call.
  if (/^\{[\s\S]*\}$/.test(trimmed) && /"(tool|name|report)"/.test(trimmed)) return '';
  return trimmed;
}

export class ChatError extends Error {
  constructor(
    message: string,
    public status?: number,
  ) {
    super(message);
  }
}

/** "us.anthropic.claude-…-v1:0" / "models/gemini-2.5-pro" → bare model name (no provider prefixes). */
export function cleanModelName(m: string | null | undefined): string | null {
  if (!m) return null;
  let t = String(m).trim();
  // Path-style vendor / router prefixes ("models/", "ollama/", "bedrock/", "openrouter/meta-llama/") —
  // the model is the last segment.
  if (t.includes('/')) t = t.slice(t.lastIndexOf('/') + 1);
  // Bedrock-style "us.anthropic.claude-…-v1:0" → "claude-…".
  t = t
    .replace(/^(us|eu|apac|global)\./i, '')
    .replace(/^(anthropic|meta|amazon|mistral|cohere|ai21|openai|google|deepseek|qwen)\./i, '')
    .replace(/-v\d+:\d+$/, '');
  return t || null;
}

/** One hop of the gateway's attribution chain (the `kstack_meta` frame, NEXUS-1349). */
export interface AttributionHop {
  instance?: string | null;
  provider?: string;
  label?: string | null;
  kind?: string;
  model?: string;
  host?: string | null;
  endpoint?: string | null;
}

/** The AI Gateway's service names (Nexus admin › AI › Gateway). */
const GATEWAY_LABELS = new Set(['Nexus AI', 'OpenAI', 'Anthropic', 'Google Gemini', 'AWS Bedrock']);
const KIND_LABELS: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  gemini: 'Google Gemini',
  google: 'Google Gemini',
  bedrock: 'AWS Bedrock',
  ollama: 'Local model (Ollama)',
  cli: 'Claude',
  agent_control: 'Claude',
};

function endpointHost(e: string | null | undefined): string | null {
  if (!e) return null;
  try {
    return new URL(e).hostname || null;
  } catch {
    return null;
  }
}

/**
 * Which AI service answered, for the line under each reply (Erwin 2026-10-28): "Nexus AI
 * (ai.kstack.app)", "Anthropic", "OpenAI", "Google Gemini", "AWS Bedrock"… Taken from the FIRST
 * hop — the service this instance called. Instance names, hosts and provider-row names stay
 * hidden; a free-text row label is shown only when it is one of the Gateway's service names.
 */
export function providerLabel(hops: AttributionHop[] | null | undefined): string | null {
  if (!hops || !hops.length) return null;
  const h = hops[0]!;
  const host = endpointHost(h.endpoint);
  const nexusAi =
    (!!host && /(^|\.)ai(-[a-z0-9]+)?\.kstack\.app$/i.test(host)) ||
    /^nexus ai$/i.test(h.label ?? '') ||
    /(nexus|k-?stack)[-_ ]?ai/i.test(h.provider ?? '');
  if (nexusAi) return `Nexus AI (${host && /kstack\.app$/i.test(host) ? host : 'ai.kstack.app'})`;
  if (h.label && GATEWAY_LABELS.has(h.label)) return h.label;
  if (host === 'api.openai.com') return 'OpenAI';
  if (host && /anthropic\.com$/i.test(host)) return 'Anthropic';
  if (host && /googleapis\.com$/i.test(host)) return 'Google Gemini';
  return (h.kind && KIND_LABELS[h.kind]) || null;
}

export interface ChatUsage {
  promptTokens?: number;
  completionTokens?: number;
  totalTokens?: number;
}

/** One streamed reply plus what the console shows under it. Timings are measured in the browser
 *  (the /v1/chat stream carries none): waitMs = request sent → first text (queue + prompt
 *  processing), genMs = first text → last text (generation). */
export interface StreamResult {
  text: string;
  waitMs: number | null;
  genMs: number | null;
  totalMs: number;
  usage: ChatUsage | null;
  model: string | null;
  /** The AI service that answered (providerLabel). The console shows it to platform admins only (forViewer). */
  provider: string | null;
}

/**
 * Streams one assistant reply. Calls onDelta with the accumulated text; resolves with the full
 * text. Throws ChatError on HTTP / stream errors.
 */
export async function streamChat(
  messages: ChatTurn[],
  onDelta: (fullText: string) => void,
  signal?: AbortSignal,
): Promise<string> {
  return (await streamChatEx(messages, onDelta, signal)).text;
}

/** streamChat + timing, token usage (when the provider reports it) and the answering model. */
export async function streamChatEx(
  messages: ChatTurn[],
  onDelta: (fullText: string) => void,
  signal?: AbortSignal,
): Promise<StreamResult> {
  const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
  let tFirst: number | null = null;
  let tLast: number | null = null;
  let usage: ChatUsage | null = null;
  let model: string | null = null;
  let provider: string | null = null;
  const requestId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `req-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const token = getToken();
  const res = await authedFetch(`${GATEWAY_BASE}/v1/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ requestId, appId: APP_ID, messages, stream: true, maxTokens: 800 }),
    signal,
  });
  if (!res.ok || !res.body) {
    let msg = `The assistant is unavailable (HTTP ${res.status}).`;
    try {
      const j = (await res.json()) as { message?: string };
      if (j.message) msg = j.message;
    } catch {
      /* keep the generic message */
    }
    throw new ChatError(msg, res.status);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let full = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf('\n\n')) >= 0) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      for (const line of frame.split('\n')) {
        if (!line.startsWith('data:')) continue;
        const data = line.slice(5).trim();
        if (!data || data === '[DONE]') continue;
        let j: {
          error?: unknown;
          type?: string;
          model?: string;
          attribution?: AttributionHop[];
          usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number };
          choices?: Array<{ delta?: { content?: string } }>;
        };
        try {
          j = JSON.parse(data);
        } catch {
          continue;
        }
        if (j.error)
          throw new ChatError(
            typeof j.error === 'string' ? j.error : 'The assistant stopped with an error.',
          );
        if (j.usage) {
          usage = {
            promptTokens: j.usage.prompt_tokens,
            completionTokens: j.usage.completion_tokens,
            totalTokens: j.usage.total_tokens,
          };
        }
        if (j.type === 'kstack_meta' && Array.isArray(j.attribution) && j.attribution.length) {
          // The answering model (last hop) and the service this instance called (first hop) —
          // instance names, hosts and provider-row names stay hidden. The service is shown to
          // platform admins only; client staff see the model (forViewer, NEXUS-1451).
          const hop = j.attribution[j.attribution.length - 1]!;
          model = cleanModelName(hop.model) || model;
          provider = providerLabel(j.attribution) || provider;
        } else if (typeof j.model === 'string' && j.model && !model) {
          model = cleanModelName(j.model);
        }
        const piece = j.choices?.[0]?.delta?.content;
        if (typeof piece === 'string' && piece) {
          const t = now();
          if (tFirst === null) tFirst = t;
          tLast = t;
          full += piece;
          onDelta(full);
        }
      }
    }
  }
  const end = now();
  return {
    text: full,
    waitMs: tFirst === null ? null : Math.round(tFirst - t0),
    genMs: tFirst === null || tLast === null ? null : Math.round(tLast - tFirst),
    totalMs: Math.round(end - t0),
    usage,
    model,
    provider,
  };
}
